//
// # PinDialog
//
// Command-palette dialog for schematic pins. Mirrors `InstanceDialog` so
// create / edit-properties / rename feel consistent across pins and
// instances. Three modes, discriminated by `pinDialog.mode.kind`:
//
//   "new":            2-step internal flow — properties (Pin Type +
//                     Direction) → name. After confirm enters AddSchPin
//                     movable mode for placement.
//   "editProperties": Direction only. Name lives in "rename"; Pin Type is
//                     a creation-time preset, not stored on the pin.
//   "rename":         Name only, pre-filled.
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { isValidPinName } from "EditorCore";
import { useWorkspace, useWorkspaceSnapshot } from "../context";
import type { PinDirection } from "../types";

// Pin type presets — pre-fill name + direction defaults at creation time.
// Not stored on `SchPinSpec`; only relevant in "new" mode.
type PinType = "signal" | "power" | "ground";

// Get default values for a pin type
function getDefaults(pinType: PinType): {
  name: string;
  direction: PinDirection;
} {
  switch (pinType) {
    case "power":
      return { name: "VDD", direction: "inout" };
    case "ground":
      return { name: "VSS", direction: "inout" };
    case "signal":
    default:
      return { name: "pin", direction: "input" };
  }
}

// Narrowing helpers for `<select>` onChange values. `as T` is lint-banned.
function asPinType(value: string): PinType | null {
  if (value === "signal" || value === "power" || value === "ground") {
    return value;
  }
  return null;
}

function asPinDirection(value: string): PinDirection | null {
  if (value === "input" || value === "output" || value === "inout") {
    return value;
  }
  return null;
}

// Internal step within "new" mode (properties then name).
type NewStep = "properties" | "name";

export interface PinDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function PinDialog({ open, onClose }: PinDialogProps) {
  const workspace = useWorkspace();
  const { pinDialog } = useWorkspaceSnapshot();

  const [pinType, setPinType] = useState<PinType>("signal");
  const [direction, setDirection] = useState<PinDirection>("input");
  const [pinName, setPinName] = useState("pin");
  // Which step of "new" mode is showing. Unused in editProperties/rename.
  const [newStep, setNewStep] = useState<NewStep>("properties");

  // Explicit focus refs — `autoFocus` doesn't fire reliably when fields
  // swap via conditional rendering, and focus dropping to document.body
  // means the backdrop's onKeyDown stops receiving Escape.
  const propertiesFocusRef = useRef<HTMLSelectElement>(null);
  const directionFocusRef = useRef<HTMLSelectElement>(null);
  const nameFocusRef = useRef<HTMLInputElement>(null);

  // Reset form state whenever the dialog opens or the mode changes.
  useEffect(() => {
    if (!open || !pinDialog) return;
    const { mode } = pinDialog;
    switch (mode.kind) {
      case "new": {
        setPinType("signal");
        setDirection("input");
        setPinName("pin");
        setNewStep("properties");
        return;
      }
      case "editProperties": {
        setDirection(mode.currentDir);
        return;
      }
      case "rename": {
        setPinName(mode.currentName);
        return;
      }
    }
  }, [open, pinDialog]);

  // Focus the active field on every step/mode change.
  useEffect(() => {
    if (!open || !pinDialog) return;
    const { mode } = pinDialog;
    if (mode.kind === "new") {
      if (newStep === "properties") {
        propertiesFocusRef.current?.focus();
      } else {
        nameFocusRef.current?.focus();
        nameFocusRef.current?.select();
      }
    } else if (mode.kind === "editProperties") {
      directionFocusRef.current?.focus();
    } else {
      nameFocusRef.current?.focus();
      nameFocusRef.current?.select();
    }
  }, [open, pinDialog, newStep]);

  // Picking a Pin Type snaps direction + the name default to its preset.
  const handlePinTypeChange = useCallback((next: PinType) => {
    setPinType(next);
    const defaults = getDefaults(next);
    setPinName(defaults.name);
    setDirection(defaults.direction);
  }, []);

  const nameValid = isValidPinName(pinName);

  // Whether the current step's submit is enabled.
  const canSubmit = (() => {
    if (!pinDialog) return false;
    const { mode } = pinDialog;
    switch (mode.kind) {
      case "new":
        return newStep === "name" ? nameValid : true;
      case "editProperties":
        return direction !== mode.currentDir;
      case "rename":
        return nameValid && pinName !== mode.currentName;
    }
  })();

  // Submit handler — behavior depends on the active mode/step.
  const handleSubmit = useCallback(() => {
    if (!pinDialog) return;
    const { mode, designIdent } = pinDialog;

    switch (mode.kind) {
      case "new": {
        if (newStep === "properties") {
          setNewStep("name");
          return;
        }
        // newStep === "name" — actually create the pin.
        if (!nameValid) return;
        workspace.createPin({
          designIdent,
          name: pinName,
          direction,
          position: mode.position,
        });
        onClose();
        return;
      }
      case "editProperties": {
        if (direction === mode.currentDir) return;
        workspace.updatePinDirection({
          designIdent,
          pinId: mode.pinId,
          newDir: direction,
        });
        onClose();
        return;
      }
      case "rename": {
        if (!nameValid || pinName === mode.currentName) return;
        workspace.renamePin({
          designIdent,
          pinId: mode.pinId,
          newName: pinName,
        });
        onClose();
        return;
      }
    }
  }, [pinDialog, newStep, nameValid, pinName, direction, workspace, onClose]);

  // Escape: in "new" mode at the name step, return to properties step
  // instead of closing. Otherwise close.
  //
  // Caveat: Escape only reaches us when focus is on a text input. With
  // focus on a <select>, EditorCore's window-level keydown handler grabs
  // Escape first (see isTextInputFocused in EditorCore/src/keys.ts) and
  // aborts the canvas mode, so this never fires. Punted — use Cancel.
  const handleEscape = useCallback(() => {
    if (pinDialog?.mode.kind === "new" && newStep === "name") {
      setNewStep("properties");
      return;
    }
    onClose();
  }, [pinDialog, newStep, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Keep keys out of EditorCore's canvas shortcut handler.
      e.stopPropagation();
      if (e.key === "Enter") {
        if (canSubmit) {
          e.preventDefault();
          handleSubmit();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleEscape();
      }
    },
    [canSubmit, handleSubmit, handleEscape]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!open || !pinDialog) return null;

  // --- Per-mode rendering ---

  const mode = pinDialog.mode;

  const title = (() => {
    switch (mode.kind) {
      case "new":
        return newStep === "properties"
          ? "Add Pin — Properties"
          : "Add Pin — Name";
      case "editProperties":
        return "Edit Pin Properties";
      case "rename":
        return "Rename Pin";
    }
  })();

  const submitLabel = (() => {
    switch (mode.kind) {
      case "new":
        return newStep === "properties" ? "Next" : "Add Pin";
      case "editProperties":
        return "Update";
      case "rename":
        return "Rename";
    }
  })();

  const hintText = (() => {
    if (mode.kind === "new" && newStep === "name") {
      return "Enter to add · Escape to go back";
    }
    return mode.kind === "new"
      ? "Enter to continue · Escape to cancel"
      : "Enter to save · Escape to cancel";
  })();

  const fieldLabelStyle: React.CSSProperties = {
    color: "var(--color-ide-text-muted)",
  };
  const inputStyle = (valid: boolean): React.CSSProperties => ({
    backgroundColor: "var(--color-ide-bg)",
    borderColor: valid ? "var(--color-ide-border)" : "var(--color-ide-error)",
    color: "var(--color-ide-text)",
  });
  const selectStyle: React.CSSProperties = {
    backgroundColor: "var(--color-ide-bg)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputClass =
    "w-full rounded border px-2.5 py-1.5 text-xs font-mono outline-none";
  const selectClass =
    "w-full rounded border px-2.5 py-1.5 text-xs outline-none cursor-pointer";

  const showPinType = mode.kind === "new" && newStep === "properties";
  const showDirection =
    (mode.kind === "new" && newStep === "properties") ||
    mode.kind === "editProperties";
  const showName =
    (mode.kind === "new" && newStep === "name") || mode.kind === "rename";
  // Back returns to step 1 of "new"; Cancel closes. Mutually exclusive.
  const showBackButton = mode.kind === "new" && newStep === "name";
  const showCancelButton = !showBackButton;

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">{title}</div>
        <div className="flex flex-col gap-3 p-3">
          {showPinType && (
            <div>
              <label
                className="block text-[10px] font-semibold uppercase tracking-widest mb-1"
                style={fieldLabelStyle}
              >
                Pin Type
              </label>
              <select
                ref={propertiesFocusRef}
                value={pinType}
                onChange={(e) => {
                  const next = asPinType(e.target.value);
                  if (next) handlePinTypeChange(next);
                }}
                className={selectClass}
                style={selectStyle}
              >
                <option value="signal">Signal</option>
                <option value="power">Power (VDD)</option>
                <option value="ground">Ground (VSS)</option>
              </select>
            </div>
          )}

          {showDirection && (
            <div>
              <label
                className="block text-[10px] font-semibold uppercase tracking-widest mb-1"
                style={fieldLabelStyle}
              >
                Direction
              </label>
              <select
                ref={directionFocusRef}
                value={direction}
                onChange={(e) => {
                  const next = asPinDirection(e.target.value);
                  if (next) setDirection(next);
                }}
                className={selectClass}
                style={selectStyle}
              >
                <option value="input">Input</option>
                <option value="output">Output</option>
                <option value="inout">Inout (Bidirectional)</option>
              </select>
            </div>
          )}

          {showName && (
            <div>
              <label
                className="block text-[10px] font-semibold uppercase tracking-widest mb-1"
                style={fieldLabelStyle}
              >
                Pin Name
              </label>
              <input
                ref={nameFocusRef}
                type="text"
                placeholder="pin"
                value={pinName}
                onChange={(e) => {
                  setPinName(e.target.value);
                }}
                className={inputClass}
                style={inputStyle(nameValid || pinName.length === 0)}
              />
              {!nameValid && pinName.length > 0 && (
                <p
                  className="text-[10px] mt-0.5"
                  style={{ color: "var(--color-ide-error)" }}
                >
                  Name must start with a letter (letters, numbers, underscores
                  only)
                </p>
              )}
            </div>
          )}
        </div>

        <div className="add-instance-footer">
          <span className="add-instance-hint">{hintText}</span>
          <div style={{ display: "flex", gap: "8px" }}>
            {showBackButton && (
              <button
                className="add-instance-btn"
                onClick={handleEscape}
                style={{
                  backgroundColor: "var(--color-ide-bg)",
                  color: "var(--color-ide-text-muted)",
                }}
              >
                Back
              </button>
            )}
            {showCancelButton && (
              <button
                className="add-instance-btn"
                onClick={onClose}
                style={{
                  backgroundColor: "var(--color-ide-bg)",
                  color: "var(--color-ide-text-muted)",
                }}
              >
                Cancel
              </button>
            )}
            <button
              className="add-instance-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
